import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, CreditCard, AlertTriangle, LogOut, Check, RefreshCw, Upload, Trash2,
  Camera, Building, Mail, Database, ExternalLink, Zap, Phone, Globe,
  Briefcase, FileText, UserCog, ShieldCheck
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { useProjects } from "../lib/projects";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "../components/ui/dialog";

const PROFILE_STORAGE_KEY = "studlyf_user_profile_data";

export default function SettingsPage() {
  const { user, setUser, logout, subscription, refreshSubscription } = useAuth();
  const { projects } = useProjects();
  const [searchParams, setSearchParams] = useSearchParams();

  // Navigation tab state synced with URL query ?tab=
  const initialTab = searchParams.get("tab") || "profile";
  const [activeSection, setActiveSection] = useState(
    ["profile", "billing", "danger"].includes(initialTab) ? initialTab : "profile"
  );

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam && ["profile", "billing", "danger"].includes(tabParam) && tabParam !== activeSection) {
      setActiveSection(tabParam);
    }
  }, [searchParams]);

  const handleTabChange = (sectionId) => {
    setActiveSection(sectionId);
    setSearchParams({ tab: sectionId }, { replace: true });
  };

  // Coupon Activation States
  const [couponCode, setCouponCode] = useState("");
  const [activatingCoupon, setActivatingCoupon] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
    } catch (e) {
      return dateStr;
    }
  };

  const handleActivateCoupon = async () => {
    if (!couponCode) return;
    setActivatingCoupon(true);
    try {
      const res = await api.post("/subscription/activate-coupon", {
        coupon_code: couponCode
      });
      if (res.data.success) {
        toast.success(res.data.message || "Pro Plan activated successfully!");
        setCouponCode("");
        await refreshSubscription();
      }
    } catch (err) {
      const errMsg = err.response?.data?.detail || "Could not activate coupon.";
      toast.error(errMsg);
    } finally {
      setActivatingCoupon(false);
    }
  };

  // ────────────────────────────────────────────────────────
  // Profile State & Persistence Logic
  // ────────────────────────────────────────────────────────
  const [profileEditing, setProfileEditing] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Helper to load stored extended profile
  const getStoredProfile = () => {
    try {
      const raw = localStorage.getItem(`${PROFILE_STORAGE_KEY}_${user?.id || user?.user_id || "guest"}`);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  };

  const [fullName, setFullName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [company, setCompany] = useState(user?.company || "");
  const [startupName, setStartupName] = useState(user?.startup_name || user?.startupName || "");
  const [website, setWebsite] = useState(user?.website || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [role, setRole] = useState(user?.role || "Founder & CEO");
  const [avatarPreview, setAvatarPreview] = useState(user?.picture || null);

  const [formErrors, setFormErrors] = useState({});

  // Sync profile when user auth object changes externally
  useEffect(() => {
    if (user) {
      setFullName(user.name || "");
      setEmail(user.email || "");
      setAvatarPreview(user.picture || null);
      setPhone(user.phone || "");
      setCompany(user.company || "");
      setStartupName(user.startup_name || user.startupName || "");
      setWebsite(user.website || "");
      setBio(user.bio || "");
      setRole(user.role || "Founder & CEO");
    }
  }, [user]);

  // Field Validation
  const validateForm = () => {
    const errors = {};

    if (!fullName.trim()) {
      errors.fullName = "Full Name is required";
    }

    if (!email.trim()) {
      errors.email = "Email Address is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = "Please enter a valid email address";
    }

    if (website.trim() && !/^(https?:\/\/)?([\w\d-]+\.)+[\w\d-]+(\/.*)?$/i.test(website.trim())) {
      errors.website = "Please enter a valid URL (e.g. https://example.com)";
    }

    if (phone.trim() && !/^[+0-9\s()--]{7,20}$/.test(phone.trim())) {
      errors.phone = "Please enter a valid phone number";
    }

    if (bio.length > 250) {
      errors.bio = "Bio cannot exceed 250 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Real-time Save Handler
  const handleSaveProfile = async () => {
    if (!validateForm()) {
      toast.error("Please fix validation errors before saving.");
      return;
    }

    setProfileSaving(true);

    const profileData = {
      name: fullName.trim(),
      email: email.trim(),
      phone: phone.trim(),
      company: company.trim(),
      startup_name: startupName.trim(),
      website: website.trim(),
      bio: bio.trim(),
      role: role.trim(),
      picture: avatarPreview
    };

    try {
      const res = await api.put("/auth/profile", profileData);
      if (res.data?.success && res.data?.user) {
        if (setUser) {
          setUser(res.data.user);
        }
        toast.success("Profile updated & synchronized successfully!");
        setProfileEditing(false);
      }
    } catch (err) {
      console.error("Profile update failed:", err);
      const errMsg = err.response?.data?.error || "Failed to update profile";
      toast.error(errMsg);
    } finally {
      setProfileSaving(false);
    }
  };

  // Helper to compress image on client side (max 400x400 px, ~35KB)
  const compressImage = (file, maxWidth = 400, maxHeight = 400, quality = 0.85) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL("image/jpeg", quality);
          resolve(dataUrl);
        };
        img.onerror = () => resolve(event.target.result);
        img.src = event.target.result;
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  };

  // Profile Photo Upload / Remove Handlers
  const handleImageFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload a valid image file (PNG, JPG, WebP)");
      return;
    }
    
    try {
      const compressedPic = await compressImage(file);
      if (!compressedPic) {
        toast.error("Could not process image file.");
        return;
      }

      setAvatarPreview(compressedPic);
      const res = await api.put("/auth/profile", { picture: compressedPic });
      if (res.data?.success && res.data?.user) {
        if (setUser) setUser(res.data.user);
        toast.success("Profile photo updated successfully!");
      }
    } catch (err) {
      console.error("Photo upload failed:", err);
      toast.error(err.response?.data?.error || "Failed to save profile picture");
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setDragActive(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageFile(e.dataTransfer.files[0]);
    }
  };

  const triggerFileSelect = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleImageFile(e.target.files[0]);
    }
  };

  const removePhoto = async () => {
    setAvatarPreview(null);
    try {
      const res = await api.put("/auth/profile", { picture: null });
      if (res.data?.success && res.data?.user) {
        if (setUser) setUser(res.data.user);
        toast.info("Profile photo cleared.");
      }
    } catch (err) {
      console.error("Failed to clear photo:", err);
      toast.error("Could not remove photo");
    }
  };

  // ────────────────────────────────────────────────────────
  // Workspace / Account Preferences State
  // ────────────────────────────────────────────────────────
  const [workspaceName] = useState("My Workspace");

  // ────────────────────────────────────────────────────────
  // Danger Zone Confirmation Dialogs State
  // ────────────────────────────────────────────────────────
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: "",
    title: "",
    description: "",
    inputValue: "",
    confirmWord: ""
  });

  const openDangerDialog = (type) => {
    let title = "";
    let description = "";
    let confirmWord = "";

    if (type === "deleteAccount") {
      title = "Delete Account permanently?";
      description = "This action is irreversible. It will erase all your projects, files, account profile and analytics. Please type DELETE below to confirm.";
      confirmWord = "DELETE";
    } else if (type === "deleteWorkspace") {
      title = "Delete Workspace?";
      description = "Are you sure you want to delete this workspace? This will terminate all collaborative projects. Please type the workspace name 'My Workspace' to confirm.";
      confirmWord = "My Workspace";
    } else if (type === "clearHistory") {
      title = "Clear AI History & Cache?";
      description = "This will wipe out all cached prompts, suggestions and history logs. Please type CLEAR to proceed.";
      confirmWord = "CLEAR";
    } else if (type === "exportData") {
      title = "Export All Account Data?";
      description = "We will package your projects and profile into a JSON archive. Type EXPORT to trigger download.";
      confirmWord = "EXPORT";
    }

    setConfirmDialog({
      open: true,
      type,
      title,
      description,
      inputValue: "",
      confirmWord
    });
  };

  const handleExecuteDangerAction = () => {
    if (confirmDialog.inputValue !== confirmDialog.confirmWord) {
      toast.error("Confirmation string does not match");
      return;
    }

    const type = confirmDialog.type;
    setConfirmDialog({ ...confirmDialog, open: false });

    toast.loading("Processing request...", { duration: 1000 });

    setTimeout(() => {
      if (type === "deleteAccount") {
        toast.success("Account successfully deleted. Redirecting...");
        setTimeout(() => logout(), 1000);
      } else if (type === "deleteWorkspace") {
        toast.success("Workspace deleted reset to default.");
      } else if (type === "clearHistory") {
        toast.success("AI History & cache cleared successfully!");
      } else if (type === "exportData") {
        const dummyData = {
          user: { name: fullName, email, phone, company, startupName, website, role },
          workspace: workspaceName,
          projectsCount: projects.length,
          exportedAt: new Date().toISOString()
        };
        const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(dummyData, null, 2));
        const dlAnchor = document.createElement("a");
        dlAnchor.setAttribute("href", dataStr);
        dlAnchor.setAttribute("download", "studlyf_data_export.json");
        dlAnchor.click();
        toast.success("Data export archive downloaded!");
      }
    }, 1000);
  };

  const navigationItems = [
    { id: "profile", label: "Profile & Account", icon: User },
    { id: "billing", label: "Billing & Plans", icon: CreditCard },
    { id: "danger", label: "Danger Zone", icon: AlertTriangle, textClass: "text-red-500" }
  ];

  return (
    <div data-testid="settings-page" className="min-h-screen text-[14.5px] leading-relaxed">
      {/* Top Header */}
      <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#7C3AED]">Workspace</p>
          <h1 className="mt-1 font-display text-3xl md:text-4xl font-semibold tracking-tighter">Settings</h1>
          <p className="mt-2 text-gray-600">Configure your personal profile details, account metrics, and billing plan.</p>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 items-start">
        {/* Navigation Sidebar */}
        <aside className="w-full lg:w-64 shrink-0 bg-white border border-gray-100/80 rounded-2xl p-4 shadow-sm relative z-10">
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 px-3 mb-2">Settings Menu</p>
          <nav className="flex flex-row lg:flex-col gap-1 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 scrollbar-none">
            {navigationItems.map((item) => {
              const active = activeSection === item.id;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => handleTabChange(item.id)}
                  className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-200 w-full text-left ${
                    active
                      ? "bg-[#F4F1FF] text-[#7C3AED] border border-gray-200/20 shadow-sm"
                      : "text-gray-500 hover:text-gray-900 hover:bg-gray-50"
                  }`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${active ? "text-[#7C3AED]" : "text-gray-400"}`} />
                  <span className={item.textClass || ""}>{item.label}</span>
                  {active && (
                    <motion.div
                      layoutId="sidebarActiveIndicator"
                      className="ml-auto w-1.5 h-1.5 rounded-full bg-current hidden lg:block"
                      transition={{ type: "spring", stiffness: 350, damping: 30 }}
                    />
                  )}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Content Pane */}
        <div className="flex-1 w-full space-y-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="space-y-8"
            >
              {/* SECTION 1: PROFILE & ACCOUNT */}
              {activeSection === "profile" && (
                <>
                  {/* Profile Information Card */}
                  <div className="rounded-2xl bg-white border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-xl bg-[#F4F1FF] text-[#7C3AED]">
                          <User className="w-5 h-5" />
                        </div>
                        <div>
                          <h2 className="text-base font-bold text-gray-900">Personal Profile Information</h2>
                          <p className="text-xs text-gray-500">Update your avatar photo, personal information, and startup details</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (profileEditing) {
                            handleSaveProfile();
                          } else {
                            setProfileEditing(true);
                          }
                        }}
                        className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all duration-200 flex items-center gap-1.5 border shadow-sm ${
                          profileEditing
                            ? "bg-emerald-500 text-white border-emerald-500 hover:bg-emerald-600"
                            : "bg-white text-gray-700 border-gray-200/80 hover:bg-gray-50"
                        }`}
                      >
                        {profileSaving ? (
                          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        ) : profileEditing ? (
                          <Check className="w-3.5 h-3.5" />
                        ) : (
                          <UserCog className="w-3.5 h-3.5" />
                        )}
                        {profileSaving ? "Saving..." : profileEditing ? "Save Changes" : "Edit Profile"}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                      {/* Left: Image Upload Zone */}
                      <div className="flex flex-col items-center justify-center p-4 border border-dashed border-gray-200/80 rounded-2xl bg-gray-50/50">
                        <p className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">Profile Picture</p>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          accept="image/*"
                          className="hidden"
                        />
                        <div className="relative group cursor-pointer" onClick={triggerFileSelect}>
                          {avatarPreview ? (
                            <img
                              src={avatarPreview}
                              alt={fullName}
                              className="w-24 h-24 rounded-full object-cover border-2 border-white shadow-md ring-4 ring-[#7C3AED]/20 transition-transform duration-200 group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] text-white flex items-center justify-center text-3xl font-semibold shadow-md ring-4 ring-[#7C3AED]/20 transition-transform duration-200 group-hover:scale-105">
                              {(fullName || "?").slice(0, 1).toUpperCase()}
                            </div>
                          )}

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              triggerFileSelect();
                            }}
                            className="absolute bottom-0 right-0 p-2 rounded-full bg-white border border-gray-100 shadow-md text-gray-600 hover:text-[#7C3AED] transition-colors"
                            title="Upload Picture"
                          >
                            <Camera className="w-4 h-4" />
                          </button>
                        </div>

                        {profileEditing ? (
                          <div className="mt-4 w-full flex flex-col items-center">
                            <div
                              onDragOver={onDragOver}
                              onDragLeave={onDragLeave}
                              onDrop={onDrop}
                              className={`w-full py-3 px-2 rounded-xl border border-dashed text-center cursor-pointer transition-all duration-200 ${
                                dragActive
                                  ? "border-[#7C3AED] bg-[#F4F1FF]/50"
                                  : "border-gray-200 hover:bg-gray-100/50"
                              }`}
                              onClick={triggerFileSelect}
                            >
                              <Upload className="w-4 h-4 mx-auto text-gray-400 mb-1" />
                              <p className="text-[10px] text-gray-500">Drag image here or click to browse</p>
                            </div>
                            {avatarPreview && (
                              <button
                                type="button"
                                onClick={removePhoto}
                                className="mt-3 text-[11px] font-bold text-red-500 hover:underline inline-flex items-center gap-1"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Remove photo
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="mt-3 text-center">
                            <p className="text-[11px] text-gray-500 font-medium cursor-pointer hover:text-[#7C3AED]" onClick={triggerFileSelect}>
                              Click avatar or camera to change picture
                            </p>
                            {avatarPreview && (
                              <button
                                type="button"
                                onClick={removePhoto}
                                className="mt-1 text-[10px] font-bold text-red-500 hover:underline inline-flex items-center gap-1"
                              >
                                <Trash2 className="w-3 h-3" /> Remove photo
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right: Profile Info Form */}
                      <div className="md:col-span-2 space-y-4">
                        {/* Row 1: Full Name & Email */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                              <User className="w-3.5 h-3.5 text-gray-400" /> Full Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={fullName}
                              disabled={!profileEditing}
                              onChange={(e) => setFullName(e.target.value)}
                              placeholder="e.g. John Doe"
                              className={`w-full px-3 py-2 border rounded-xl text-xs transition-all outline-none ${
                                formErrors.fullName ? "border-red-400 bg-red-50/20" : ""
                              } ${
                                profileEditing
                                  ? "bg-white border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20"
                                  : "bg-gray-50/50 border-gray-100 text-gray-700 cursor-not-allowed"
                              }`}
                            />
                            {formErrors.fullName && <p className="mt-1 text-[10px] text-red-500 font-semibold">{formErrors.fullName}</p>}
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5 text-gray-400" /> Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="email"
                              value={email}
                              disabled={!profileEditing}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="john@example.com"
                              className={`w-full px-3 py-2 border rounded-xl text-xs transition-all outline-none ${
                                formErrors.email ? "border-red-400 bg-red-50/20" : ""
                              } ${
                                profileEditing
                                  ? "bg-white border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20"
                                  : "bg-gray-50/50 border-gray-100 text-gray-700 cursor-not-allowed"
                              }`}
                            />
                            {formErrors.email && <p className="mt-1 text-[10px] text-red-500 font-semibold">{formErrors.email}</p>}
                          </div>
                        </div>

                        {/* Row 2: Phone & Role */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                              <Phone className="w-3.5 h-3.5 text-gray-400" /> Phone Number
                            </label>
                            <input
                              type="text"
                              value={phone}
                              disabled={!profileEditing}
                              onChange={(e) => setPhone(e.target.value)}
                              placeholder="+1 (555) 000-0000"
                              className={`w-full px-3 py-2 border rounded-xl text-xs transition-all outline-none ${
                                formErrors.phone ? "border-red-400 bg-red-50/20" : ""
                              } ${
                                profileEditing
                                  ? "bg-white border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20"
                                  : "bg-gray-50/50 border-gray-100 text-gray-700 cursor-not-allowed"
                              }`}
                            />
                            {formErrors.phone && <p className="mt-1 text-[10px] text-red-500 font-semibold">{formErrors.phone}</p>}
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                              <Briefcase className="w-3.5 h-3.5 text-gray-400" /> Professional Role
                            </label>
                            <select
                              value={role}
                              disabled={!profileEditing}
                              onChange={(e) => setRole(e.target.value)}
                              className={`w-full px-3 py-2 border rounded-xl text-xs transition-all outline-none ${
                                profileEditing
                                  ? "bg-white border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20"
                                  : "bg-gray-50/50 border-gray-100 text-gray-700 cursor-not-allowed"
                              }`}
                            >
                              <option value="Founder & CEO">Founder & CEO</option>
                              <option value="Co-Founder">Co-Founder</option>
                              <option value="Student Founder">Student Founder</option>
                              <option value="Product Manager">Product Manager</option>
                              <option value="Researcher">Researcher</option>
                              <option value="Investor / VC">Investor / VC</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>
                        </div>

                        {/* Row 3: Company & Startup Name */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                              <Building className="w-3.5 h-3.5 text-gray-400" /> College / Company
                            </label>
                            <input
                              type="text"
                              value={company}
                              disabled={!profileEditing}
                              onChange={(e) => setCompany(e.target.value)}
                              placeholder="e.g. MIT / Acme Corp"
                              className={`w-full px-3 py-2 border rounded-xl text-xs transition-all outline-none ${
                                profileEditing
                                  ? "bg-white border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20"
                                  : "bg-gray-50/50 border-gray-100 text-gray-700 cursor-not-allowed"
                              }`}
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                              <ShieldCheck className="w-3.5 h-3.5 text-gray-400" /> Startup Name
                            </label>
                            <input
                              type="text"
                              value={startupName}
                              disabled={!profileEditing}
                              onChange={(e) => setStartupName(e.target.value)}
                              placeholder="e.g. Lumen AI Labs"
                              className={`w-full px-3 py-2 border rounded-xl text-xs transition-all outline-none ${
                                profileEditing
                                  ? "bg-white border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20"
                                  : "bg-gray-50/50 border-gray-100 text-gray-700 cursor-not-allowed"
                              }`}
                            />
                          </div>
                        </div>

                        {/* Row 4: Website URL */}
                        <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                            <Globe className="w-3.5 h-3.5 text-gray-400" /> Website URL
                          </label>
                          <input
                            type="text"
                            value={website}
                            disabled={!profileEditing}
                            onChange={(e) => setWebsite(e.target.value)}
                            placeholder="https://yourstartup.com"
                            className={`w-full px-3 py-2 border rounded-xl text-xs transition-all outline-none ${
                              formErrors.website ? "border-red-400 bg-red-50/20" : ""
                            } ${
                              profileEditing
                                ? "bg-white border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20"
                                : "bg-gray-50/50 border-gray-100 text-gray-700 cursor-not-allowed"
                            }`}
                          />
                          {formErrors.website && <p className="mt-1 text-[10px] text-red-500 font-semibold">{formErrors.website}</p>}
                        </div>

                        {/* Row 5: Bio with character counter */}
                        <div>
                          <div className="flex justify-between items-center mb-1.5">
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1">
                              <FileText className="w-3.5 h-3.5 text-gray-400" /> Short Bio
                            </label>
                            {profileEditing && (
                              <span className={`text-[10px] font-bold ${bio.length > 250 ? "text-red-500" : "text-gray-400"}`}>
                                {bio.length} / 250
                              </span>
                            )}
                          </div>
                          <textarea
                            rows={3}
                            value={bio}
                            disabled={!profileEditing}
                            onChange={(e) => setBio(e.target.value)}
                            placeholder="Tell us a little bit about yourself or your startup..."
                            className={`w-full px-3 py-2 border rounded-xl text-xs transition-all outline-none resize-none ${
                              formErrors.bio ? "border-red-400 bg-red-50/20" : ""
                            } ${
                              profileEditing
                                ? "bg-white border-gray-200 focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20"
                                : "bg-gray-50/50 border-gray-100 text-gray-700 cursor-not-allowed"
                            }`}
                          />
                          {formErrors.bio && <p className="mt-1 text-[10px] text-red-500 font-semibold">{formErrors.bio}</p>}
                        </div>

                        {profileEditing && (
                          <div className="flex justify-end gap-3 pt-2">
                            <button
                              type="button"
                              onClick={() => {
                                const s = getStoredProfile();
                                setFullName(user?.name || s.fullName || "John Doe");
                                setEmail(user?.email || s.email || "john.doe@university.edu");
                                setPhone(s.phone || "+1 (555) 234-5678");
                                setCompany(s.company || "Massachusetts Institute of Technology");
                                setStartupName(s.startupName || "Lumen AI Labs");
                                setWebsite(s.website || "https://lumenlabs.ai");
                                setBio(s.bio || "Building next-generation AI tools.");
                                setRole(s.role || "Founder & CEO");
                                setAvatarPreview(user?.picture || s.avatarPreview || null);
                                setFormErrors({});
                                setProfileEditing(false);
                              }}
                              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-xs font-semibold hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={handleSaveProfile}
                              className="px-4 py-2 text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm bg-[#7C3AED] hover:bg-[#6D28D9]"
                            >
                              {profileSaving && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                              Save Changes
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Account System Details Card */}
                  <div className="rounded-2xl bg-white border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-100">
                      <div className="p-2 rounded-xl bg-[#F4F1FF] text-[#7C3AED]">
                        <Database className="w-5 h-5" />
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-gray-900">Account System Details</h2>
                        <p className="text-xs text-gray-500">Live indicators, plans, database statistics, and limits</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-2xl text-center hover:bg-white hover:border-gray-200 transition-all duration-200">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">User ID</p>
                        <p className="font-display font-bold text-sm text-gray-800 mt-1 truncate" title={user?.id || user?.user_id || "usr_2910a9f14"}>
                          {user?.id || user?.user_id || "usr_2910a9f14"}
                        </p>
                        <p className="text-[10px] text-emerald-500 mt-1 flex items-center justify-center gap-1 font-semibold">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Active Session
                        </p>
                      </div>

                      <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-2xl text-center hover:bg-white hover:border-gray-200 transition-all duration-200">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Joined Date</p>
                        <p className="font-display font-bold text-sm text-gray-800 mt-1">July 2, 2026</p>
                        <p className="text-[10px] text-gray-500 mt-1 font-semibold">Verified User</p>
                      </div>

                      <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-2xl text-center hover:bg-white hover:border-gray-200 transition-all duration-200">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Workspace Name</p>
                        <p className="font-display font-bold text-sm text-gray-800 mt-1 truncate" title={workspaceName}>{workspaceName}</p>
                        <p className="text-[10px] text-gray-500 mt-1 font-semibold">Creator Owner</p>
                      </div>

                      <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-2xl text-center hover:bg-white hover:border-gray-200 transition-all duration-200">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Projects Allocated</p>
                        <p className="font-display font-bold text-lg text-gray-800 mt-0.5">{projects.length} / 10</p>
                        <p className="text-[10px] text-gray-500 mt-0.5 font-semibold">Usage Limits</p>
                      </div>

                      <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-2xl text-center hover:bg-white hover:border-gray-200 transition-all duration-200">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">AI Generations</p>
                        <p className="font-display font-bold text-lg text-gray-800 mt-0.5">420 credits</p>
                        <p className="text-[10px] text-gray-500 mt-0.5 font-semibold">Used this month</p>
                      </div>

                      <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-2xl text-center hover:bg-white hover:border-gray-200 transition-all duration-200">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Last Login</p>
                        <p className="font-display font-bold text-sm text-gray-800 mt-1 truncate">Today, 12:46 PM</p>
                        <p className="text-[10px] text-gray-500 mt-1 font-semibold">Current Device</p>
                      </div>

                      <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-2xl text-center hover:bg-white hover:border-gray-200 transition-all duration-200">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Account Plan</p>
                        <span className="inline-block mt-1 px-2.5 py-0.5 text-[10px] font-bold rounded-full bg-[#F4F1FF] text-[#7C3AED] border border-[#7C3AED]/20 uppercase">
                          {subscription?.plan_name || "FREE PLAN"}
                        </span>
                        <p className="text-[10px] text-gray-500 mt-0.5 font-semibold">Status: Active</p>
                      </div>

                      <div className="p-4 bg-gray-50/50 border border-gray-100 rounded-2xl text-center hover:bg-white hover:border-gray-200 transition-all duration-200">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Account Status</p>
                        <span className="inline-block mt-1 px-2.5 py-0.5 text-[10px] font-bold rounded-full bg-emerald-50 text-emerald-600 border border-emerald-500/20">
                          ACTIVE
                        </span>
                        <p className="text-[10px] text-gray-500 mt-0.5 font-semibold">All systems nominal</p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* SECTION 2: BILLING & PLANS */}
              {activeSection === "billing" && (
                <>
                  {/* Subscription details Card */}
                  <div className="rounded-2xl border border-[#7C3AED]/20 bg-gradient-to-br from-[#7C3AED]/5 to-[#EC4899]/5 p-6 shadow-sm relative overflow-hidden">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 relative z-10 pb-6 border-b border-[#7C3AED]/10">
                      <div>
                        <span className="inline-block px-3 py-1 text-[10px] font-extrabold rounded-full bg-[#F4F1FF] text-[#7C3AED] border border-[#7C3AED]/20 uppercase">
                          {subscription?.plan_name === "STUDLYF Pro" ? "PRO ACCOUNT" : "FREE ACCOUNT"}
                        </span>
                        <h2 className="text-xl font-display font-extrabold text-gray-900 mt-2">
                          {subscription?.plan_name === "STUDLYF Pro" ? "Premium Pro Tier" : "Free Plan Active"}
                        </h2>
                        <p className="text-xs text-gray-600 mt-1">
                          {subscription?.plan_name === "STUDLYF Pro"
                            ? "Enjoy unrestricted access to all Pro workspace tools."
                            : "Upgrade today to unlock advanced startup generators & metrics."}
                        </p>
                      </div>

                      <div className="bg-white border border-[#7C3AED]/20 p-4 rounded-2xl shadow-sm text-center shrink-0">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Time Remaining</p>
                        <p className="font-display font-black text-3xl brand-gradient-text mt-1">
                          {subscription?.plan_name === "STUDLYF Pro" && subscription?.status === "active"
                            ? `${subscription.remaining_days} Days`
                            : "0 Days"}
                        </p>
                        <p className="text-[9px] text-gray-500 font-semibold mt-1">
                          {subscription?.plan_name === "STUDLYF Pro" && subscription?.status === "active"
                            ? `Ends on ${formatDate(subscription.expiry_date)}`
                            : "No active premium plan"}
                        </p>
                      </div>
                    </div>

                    {/* SUBSCRIPTION DETAILS TABLE */}
                    <div className="mt-6 bg-white border border-gray-150 rounded-2xl p-4 relative z-10">
                      <h3 className="text-xs font-bold text-gray-950 uppercase tracking-wider mb-3">Subscription Details</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-6 gap-4">
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase">Current Plan</p>
                          <p className="text-xs font-bold text-gray-900 mt-0.5">
                            {subscription?.plan_name || "Free"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase">Coupon Used</p>
                          <p className="text-xs font-bold text-gray-900 mt-0.5 font-mono">
                            {subscription?.coupon_code || (subscription?.plan_name === "STUDLYF Pro" ? "STUDLYF499" : "None")}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase">Status</p>
                          <p className="text-xs font-bold text-gray-900 mt-0.5 capitalize">
                            {subscription?.status || "None"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase">Activation Date</p>
                          <p className="text-xs font-bold text-gray-900 mt-0.5">
                            {formatDate(subscription?.start_date)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase">Expiry Date</p>
                          <p className="text-xs font-bold text-gray-900 mt-0.5">
                            {formatDate(subscription?.expiry_date)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase">Remaining Days</p>
                          <p className="text-xs font-bold text-gray-900 mt-0.5">
                            {subscription?.remaining_days ?? 0} Days
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* EXPIRED ALERT BOX */}
                    {subscription?.status === "expired" && (
                      <div className="mt-4 p-3.5 bg-red-50 border border-red-200 rounded-xl flex items-center justify-between gap-2.5 text-red-800 text-xs relative z-10">
                        <div className="flex items-center gap-2.5">
                          <AlertTriangle className="w-4.5 h-4.5 text-red-600 shrink-0" />
                          <div>
                            <p className="font-bold">Your STUDLYF Pro access has expired.</p>
                            <p className="text-[11px] text-red-700 mt-0.5">Please upgrade to the ₹499 Pro Plan to regain full access.</p>
                          </div>
                        </div>
                        <button
                          onClick={() => window.location.href = "/#pricing"}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg text-xs shrink-0"
                        >
                          Upgrade to Pro — ₹499
                        </button>
                      </div>
                    )}

                    {/* EXPIRY WARNING BOX (LESS THAN 5 DAYS) */}
                    {subscription?.plan_name === "STUDLYF Pro" && subscription?.status === "active" && subscription?.remaining_days <= 5 && (
                      <div className="mt-4 p-3.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2.5 text-amber-800 text-xs relative z-10">
                        <AlertTriangle className="w-4.5 h-4.5 text-amber-600 shrink-0" />
                        <div>
                          <p className="font-bold">Pro Subscription Expiry Warning</p>
                          <p className="text-[11px] text-amber-700 mt-0.5">Your Pro subscription expires in {subscription.remaining_days} days.</p>
                        </div>
                      </div>
                    )}

                    {/* ACTIVATE PRO ACCESS CODE INPUT */}
                    <div className="mt-8 pt-8 border-t border-gray-150 relative z-10">
                      <h3 className="text-sm font-bold text-gray-900 mb-1">Have a Pro Access Code?</h3>
                      <p className="text-xs text-gray-500 mb-4">Enter your coupon code below to unlock 30 days of full STUDLYF Pro access.</p>

                      <div className="flex flex-col sm:flex-row gap-3 max-w-md">
                        <input
                          type="text"
                          placeholder="Enter Coupon Code"
                          value={couponCode}
                          onChange={(e) => setCouponCode(e.target.value.toUpperCase().trim())}
                          disabled={activatingCoupon}
                          className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-xs outline-none bg-white focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 uppercase font-mono tracking-wider"
                        />
                        <button
                          type="button"
                          onClick={handleActivateCoupon}
                          disabled={activatingCoupon || !couponCode}
                          className="px-6 py-2.5 bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white hover:opacity-90 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-1.5 shrink-0 disabled:opacity-50"
                        >
                          {activatingCoupon ? (
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Zap className="w-3.5 h-3.5 fill-current" />
                          )}
                          Activate
                        </button>
                      </div>
                    </div>

                    <div className="mt-8 flex justify-end gap-3 relative z-10 pt-4 border-t border-gray-100">
                      <button
                        type="button"
                        onClick={() => toast.info("Invoice history is displayed below.")}
                        className="px-4 py-2 border border-[#7C3AED]/20 text-gray-700 bg-white rounded-xl text-xs font-bold hover:bg-gray-50 transition-colors shadow-sm"
                      >
                        Billing Invoices
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          window.location.href = "/#pricing";
                        }}
                        className="glow-button rounded-xl px-5 py-2 text-xs font-extrabold flex items-center gap-1.5"
                      >
                        <Zap className="w-3.5 h-3.5 fill-current" /> Upgrade to Pro – ₹499
                      </button>
                    </div>
                  </div>

                  {/* Billing Invoices / History */}
                  <div className="rounded-2xl bg-white border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-xl bg-[#F4F1FF] text-[#7C3AED]">
                          <CreditCard className="w-5 h-5" />
                        </div>
                        <div>
                          <h2 className="text-base font-bold text-gray-900">Billing History</h2>
                          <p className="text-xs text-gray-500">Review your past invoices, statuses and dates</p>
                        </div>
                      </div>
                    </div>

                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="border-b border-gray-100 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            <th className="pb-3 font-semibold">Invoice ID</th>
                            <th className="pb-3 font-semibold">Billing Period</th>
                            <th className="pb-3 font-semibold">Amount</th>
                            <th className="pb-3 font-semibold">Payment Status</th>
                            <th className="pb-3 font-semibold text-right">Receipt</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-xs">
                          <tr>
                            <td className="py-3 font-bold text-gray-900">INV-8120</td>
                            <td className="py-3 text-gray-500">Jun 2, 2026 - Jul 2, 2026</td>
                            <td className="py-3 font-semibold text-gray-800">$0.00 (Pro Trial)</td>
                            <td className="py-3">
                              <span className="px-2.5 py-0.5 text-[9px] font-extrabold rounded-full bg-emerald-50 text-emerald-600 border border-emerald-500/20">
                                PAID
                              </span>
                            </td>
                            <td className="py-3 text-right">
                              <button
                                onClick={() => toast.success("Receipt downloaded!")}
                                className="text-[10px] font-bold text-[#7C3AED] hover:underline flex items-center gap-0.5 ml-auto"
                              >
                                View PDF <ExternalLink className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                          <tr>
                            <td className="py-3 font-bold text-gray-900">INV-6019</td>
                            <td className="py-3 text-gray-500">May 2, 2026 - Jun 2, 2026</td>
                            <td className="py-3 font-semibold text-gray-800">$0.00 (Pro Trial)</td>
                            <td className="py-3">
                              <span className="px-2.5 py-0.5 text-[9px] font-extrabold rounded-full bg-emerald-50 text-emerald-600 border border-emerald-500/20">
                                PAID
                              </span>
                            </td>
                            <td className="py-3 text-right">
                              <button
                                onClick={() => toast.success("Receipt downloaded!")}
                                className="text-[10px] font-bold text-[#7C3AED] hover:underline flex items-center gap-0.5 ml-auto"
                              >
                                View PDF <ExternalLink className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {/* SECTION 3: DANGER ZONE */}
              {activeSection === "danger" && (
                <>
                  <div className="rounded-2xl border border-red-200 bg-red-50/10 p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-2 mb-6 pb-4 border-b border-red-100">
                      <div className="p-2 rounded-xl bg-red-100 text-red-600">
                        <AlertTriangle className="w-5 h-5" />
                      </div>
                      <div>
                        <h2 className="text-base font-bold text-red-950">Destructive Actions Danger Zone</h2>
                        <p className="text-xs text-red-600/80">Irreversible actions that modify or delete workspace settings</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {/* Log out */}
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-red-100 rounded-2xl bg-white">
                        <div>
                          <p className="text-xs font-bold text-gray-900">Logout Current Session</p>
                          <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">Safely close this active session and clear token cache.</p>
                        </div>
                        <button
                          onClick={logout}
                          className="px-4 py-2 bg-gray-900 text-white hover:bg-gray-800 rounded-xl text-xs font-semibold shadow-sm transition-all shrink-0 self-start md:self-center"
                          data-testid="settings-logout"
                        >
                          Log out
                        </button>
                      </div>

                      {/* Clear History */}
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-red-100 rounded-2xl bg-white">
                        <div>
                          <p className="text-xs font-bold text-gray-900">Wipe AI Prompt Logs & Cache</p>
                          <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">Permanently delete prompt completion records, suggestions, and workspace caches.</p>
                        </div>
                        <button
                          onClick={() => openDangerDialog("clearHistory")}
                          className="px-4 py-2 border border-red-200 text-red-600 hover:bg-red-55 rounded-xl text-xs font-semibold shadow-sm transition-all shrink-0 self-start md:self-center"
                        >
                          Clear AI History
                        </button>
                      </div>

                      {/* Export Account Data */}
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-red-100 rounded-2xl bg-white">
                        <div>
                          <p className="text-xs font-bold text-gray-900">Export All Account Data</p>
                          <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">Package all created projects and user profile into a downloadable JSON bundle.</p>
                        </div>
                        <button
                          onClick={() => openDangerDialog("exportData")}
                          className="px-4 py-2 border border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl text-xs font-semibold shadow-sm transition-all shrink-0 self-start md:self-center"
                        >
                          Export Data Archive
                        </button>
                      </div>

                      {/* Delete Workspace */}
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-red-100 rounded-2xl bg-white">
                        <div>
                          <p className="text-xs font-bold text-gray-900">Delete Workspace</p>
                          <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">Delete &apos;{workspaceName}&apos; along with all allocated projects and analytics.</p>
                        </div>
                        <button
                          onClick={() => openDangerDialog("deleteWorkspace")}
                          className="px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-xl text-xs font-semibold shadow-sm transition-all shrink-0 self-start md:self-center"
                        >
                          Delete Workspace
                        </button>
                      </div>

                      {/* Delete Account */}
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-4 border border-red-100 rounded-2xl bg-white">
                        <div>
                          <p className="text-xs font-bold text-red-600">Delete User Account</p>
                          <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">Deletes profile picture, user record, analytics, files, and links. This cannot be undone.</p>
                        </div>
                        <button
                          onClick={() => openDangerDialog("deleteAccount")}
                          className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-xl text-xs font-semibold shadow-sm transition-all shrink-0 self-start md:self-center"
                        >
                          Delete Account
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* DANGER CONFIRMATION DIALOG */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
        <DialogContent className="max-w-md bg-white border border-gray-100 shadow-xl rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
              <span>{confirmDialog.title}</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500 leading-normal mt-2">
              {confirmDialog.description}
            </DialogDescription>
          </DialogHeader>

          <div className="my-4">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">
              Type <span className="font-extrabold text-gray-700 font-mono">&quot;{confirmDialog.confirmWord}&quot;</span> below
            </p>
            <input
              type="text"
              value={confirmDialog.inputValue}
              onChange={(e) => setConfirmDialog({ ...confirmDialog, inputValue: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/20"
              placeholder="Confirm instruction..."
            />
          </div>

          <DialogFooter className="flex gap-2 justify-end">
            <button
              onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
              className="px-4 py-2 border border-gray-200 text-gray-600 hover:bg-gray-50 rounded-xl text-xs font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleExecuteDangerAction}
              disabled={confirmDialog.inputValue !== confirmDialog.confirmWord}
              className={`px-4 py-2 text-white rounded-xl text-xs font-bold transition-all shadow-sm ${
                confirmDialog.inputValue === confirmDialog.confirmWord
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Confirm Destructive Action
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
